import { OverdueUsersReport } from '@/client/dashboard/types';
import { FilterNamesEnum } from '@/client/helpers/filter.helper';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import {
  createCustomTooltip,
  hideTooltipForNavigation,
} from '@/ui/charts/charts.helper';
import { FlexContainer } from '@/ui/styled-ui';
import { getKPITimeBasedLabels, parsePeriodToDate } from '@/utils/helpers';
import {
  colorBlackMain,
  colorBlueDark,
  colorGrayDark,
  platformFontFamily,
} from '@/utils/helpers/css-variables.helper';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  Title,
  Tooltip,
} from 'chart.js';
import { Chart } from 'primereact/chart';
import { Skeleton } from 'primereact/skeleton';
import React, { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

const ChartWrapper = styled.div`
  height: 100%;
  width: 100%;
  flex: 1;
`;

const ChartContainer = styled.div<{ isDisabled?: boolean }>`
  overflow-x: scroll;
  width: 100%;
  position: relative;
  background: var(--white-main);
  margin-top: ${({ isDisabled }) =>
    isDisabled ? '0' : 'var(--default-padding)'};
`;

const StyledBarChart = styled(Chart)`
  width: 100%;
  height: 100%;
`;

const DisabledOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.7);
  z-index: 2;
  pointer-events: all;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  text-align: center;
  padding: var(--default-padding);
`;

type OverdueUsersChartProps = {
  id?: string;
  fromDate?: Date | undefined;
  toDate?: Date | undefined;
  reportData?: OverdueUsersReport[];
  isLoading?: boolean;
  isDisabled?: boolean;
  disabledOverlayContent?: ReactNode;
};

const getMonthIndexFromPeriod = (period: string | undefined): number | null => {
  if (!period) return null;

  const parsedDate = parsePeriodToDate(period);
  if (!parsedDate) return null;
  return parsedDate.getMonth();
};

const getDataMappedToLabels = (
  reportData: OverdueUsersReport[] | undefined,
  labels: { [key: number]: string }[],
): {
  label: string;
  value: OverdueUsersReport | null;
}[] => {
  if (!reportData || !labels) {
    return [];
  }

  return labels.map((labelObject: { [key: number]: string }) => {
    const matchingItem = reportData?.find((item) => {
      const monthIndex = getMonthIndexFromPeriod(item?.period);
      return (
        monthIndex !== null &&
        monthIndex === Number(Object.keys(labelObject)[0])
      );
    });
    return {
      label: Object.values(labelObject)[0],
      value: matchingItem || null,
    };
  });
};

export const OverdueUsersChart = ({
  id,
  fromDate,
  toDate,
  reportData,
  isLoading,
  isDisabled = false,
  disabledOverlayContent,
}: OverdueUsersChartProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [chartData, setChartData] = useState({});
  const [chartOptions, setChartOptions] = useState({});
  const [datasets, setDatasets] = useState<
    {
      label: string;
      value: OverdueUsersReport | null;
    }[]
  >([]);
  const [isChartDataLoading, setIsChartDataLoading] = useState(false);
  const docStyle = getComputedStyle(document.documentElement);
  const textColorSecondary = colorBlackMain(docStyle);

  const performanceOverTimeLabels = getKPITimeBasedLabels(
    fromDate,
    toDate,
    reportData,
    t,
    isDisabled,
  );

  const hasNoData =
    !reportData || reportData.length === 0 || datasets.length === 0;

  useEffect(() => {
    if (!reportData || !performanceOverTimeLabels) return;
    const datasetsData = getDataMappedToLabels(
      reportData,
      performanceOverTimeLabels,
    );

    setIsChartDataLoading(true);
    setDatasets(datasetsData);
  }, [reportData]);

  useEffect(() => {
    return () => {
      const tooltipId = `chart-tooltip-${id}-chart`;
      hideTooltipForNavigation(tooltipId);
    };
  }, [id]);

  useEffect(() => {
    const overdueUsersData =
      datasets?.map((item) => {
        return Number(item.value?.usersCount || 0);
      }) || [];

    const allValues = overdueUsersData.filter(
      (val): val is number => val !== null,
    );

    const maxDataValue = allValues.length > 0 ? Math.max(...allValues) : 0;
    const roundedMax = Math.ceil(maxDataValue / 10) * 10;

    const data = {
      labels: datasets?.map((item) => item.label),
      datasets: [
        {
          type: 'bar',
          label: t('dashboard.awareness.overdue.title'),
          backgroundColor: colorBlueDark(docStyle),
          data: datasets?.map((item) =>
            item.value?.usersCount !== undefined &&
            item.value?.usersCount !== null
              ? Number(item.value?.usersCount || 0).toFixed(0)
              : null,
          ),
          hoverBackgroundColor: colorBlueDark(docStyle) + 'CC',
        },
      ],
    };

    const options = {
      maintainAspectRatio: false,
      aspectRatio: 0.6,
      interaction: {
        mode: 'index' as const,
        intersect: false,
      },
      plugins: {
        tooltip: {
          enabled: false,
          external: (context: any) =>
            createCustomTooltip(
              context,
              t,
              datasets,
              false,
              false,
              false,
              true,
            ),
        },
        legend: {
          display: false,
        },
        datalabels: {
          display: false,
        },
      },
      scales: {
        x: {
          ticks: {
            color: textColorSecondary,
            font: {
              family: platformFontFamily(docStyle),
            },
          },
          grid: {
            display: true,
            color: colorGrayDark(docStyle),
            lineWidth: 1,
            dash: [10, 10],
            drawOnChartArea: true,
          },
          barPercentage: 0.5,
          categoryPercentage: 0.6,
          border: {
            dash: [5, 5],
            display: false,
          },
        },
        y: {
          min: 0,
          max: roundedMax,
          ticks: {
            color: textColorSecondary,
            stepSize: 20,
            font: {
              family: platformFontFamily(docStyle),
            },
          },
          grid: {
            display: true,
            color: colorGrayDark(docStyle),
            lineWidth: 1,
            dash: [10, 10],
            drawOnChartArea: true,
          },
          border: {
            dash: [5, 5],
            display: false,
          },
        },
      },
      elements: {
        bar: {
          borderRadius: {
            topLeft: 5,
            topRight: 5,
          },
        },
        line: {
          borderDash: [7, 5],
        },
      },
      onHover: (event: any, activeElements: any[]) => {
        if (!event?.native?.target?.style) return;
        event.native.target.style.cursor =
          activeElements.length > 0 ? 'pointer' : 'default';
      },
      onClick: (event: any, activeElements: any[], chart: any) => {
        if (!activeElements || !chart?.canvas) return;

        if (activeElements.length > 0) {
          const tooltipId = `chart-tooltip-${chart.canvas.id || 'default'}`;
          hideTooltipForNavigation(tooltipId);

          const urlParams = new URLSearchParams({
            filters: JSON.stringify({
              [FilterNamesEnum.SUMMARY_HAVE_OVERDUE_COURSES]: 'yes',
            }),
          });
          navigate(
            `${RedirectPaths[
              RedirectPathsEnum.REPORTS_SUMMARY
            ]()}?${urlParams.toString()}`,
            { state: { isDashboardLink: true } },
          );
        }
      },
    };

    setChartData(data);
    setChartOptions(options);
    setIsChartDataLoading(false);
  }, [datasets, reportData]);

  return (
    <ChartContainer id={id} isDisabled={isDisabled}>
      {isDisabled && (
        <DisabledOverlay>{disabledOverlayContent}</DisabledOverlay>
      )}
      <ChartWrapper>
        {isLoading || isChartDataLoading ? (
          <Skeleton height="100%" width="100%" />
        ) : hasNoData ? (
          <FlexContainer
            height="100%"
            direction="column"
            justify="center"
            align="center"
          >
            <p className="text-center">{t('dashboard.noData')}</p>
          </FlexContainer>
        ) : (
          <StyledBarChart
            type="line"
            data={chartData}
            options={chartOptions}
            id={`${id}-chart`}
          />
        )}
      </ChartWrapper>
    </ChartContainer>
  );
};
